import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Building2,
  Send,
  MessageSquare,
  Mail,
  Calendar,
  ShieldAlert,
  FileText,
  CheckCircle2,
  Clock,
  Settings,
  RefreshCw,
  AlertTriangle,
  ExternalLink,
  Copy,
  Download,
  Lock,
  DollarSign,
  AlertCircle,
  Upload,
  CreditCard,
  FileSpreadsheet,
  Users,
  TrendingUp,
  CheckSquare,
  Square,
  Check,
  FileDown,
  BarChart3,
  PieChart as PieIcon,
  Plus,
  Trash2,
  Edit,
  Search,
  UserCheck,
  Eye,
  Code,
  CheckCheck,
  ShieldCheck,
  Sparkles,
  Handshake,
  Gavel,
  Calculator,
  Zap,
  Cpu,
  MapPin,
  Info,
} from 'lucide-react';
import PaymentAgreementsTab from '@/components/dashboard/agreements/PaymentAgreementsTab';
import LitigationTab from '@/components/dashboard/litigation/LitigationTab';
import FinancialLiquidadorJudicial from '@/components/dashboard/liquidador/FinancialLiquidadorJudicial';
import CobranzaSchedulerHub from '@/components/dashboard/scheduler/CobranzaSchedulerHub';
import { WhatsAppApiService, WhatsAppApiConfig } from '@/services/whatsappApiService';
import WhatsAppConfigModal from '@/components/dashboard/whatsapp/WhatsAppConfigModal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import DashboardBreadcrumbs from '@/components/ui/DashboardBreadcrumbs';
import { useApiClient } from '@/hooks/use-api-client';
import { useAuth } from '@/hooks/useAuth';
import { API_BASE_URL } from '@/config';
import { getAccessToken } from '@/lib/auth-utils';
import { toast } from 'sonner';

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend
} from 'recharts';

interface DemandLetterResult {
  copropiedad_nombre: string;
  unidad_privada: string;
  propietario_nombre: string;
  saldo_total: number;
  etapa_cobro: string;
  letter: {
    content: string;
    sections?: string[];
  };
  whatsapp_message: string;
  email_preview: {
    subject: string;
    summary: string;
  };
  payment_link?: string;
  colombian_compliance: any;
}

interface NotificationLog {
  id: string;
  copropiedad_nombre: string;
  direccion?: string;
  propietario_nombre: string;
  unidad_privada: string;
  canal: string;
  destinatario: string;
  etapa_cobro: string;
  saldo_total: number;
  dias_mora: number;
  estado: string;
  asunto: string;
  payment_link?: string;
  direct_link?: string;
  advertencia_ley_2300?: string;
  created_at: string;
}

interface CopropiedadSettings {
  copropiedad_id: string;
  nombre_copropiedad: string;
  nit: string;
  administrador_nombre: string;
  email_contacto: string;
  telefono_contacto: string;
  cuenta_bancaria_info: string;
  dia_corte_mes: number;
  frecuencia_notificacion: string;
  umbrales_dias_mora: number[];
  canales_habilitados: string[];
  respetar_ley_2300: boolean;
  tasa_interes_tipo: string;
  propietarios_seleccionados?: string[];
  modo_seleccion_propietarios?: 'todos' | 'manual';
}

interface DebtorRecord {
  id?: string;
  copropiedad_id?: string;
  copropiedad_nombre?: string;
  direccion?: string;
  unidad_privada: string;
  propietario_nombre: string;
  propietario_id?: string;
  telefono?: string;
  email?: string;
  saldo_capital: number;
  saldo_intereses: number;
  saldo_total?: number;
  dias_mora: number;
  periodos_adeudados?: string[];
  etapa_cobro: string;
  notas?: string;
  payment_link?: string;
}

interface BatchSummary {
  total_unidades: number;
  total_deuda: number;
  semaforo: {
    verde: number;
    amarillo: number;
    rojo: number;
  };
}

const COLORS_SEMAFORO = {
  verde: '#10b981',
  amarillo: '#f59e0b',
  rojo: '#e11d48'
};

export default function LegalNotificationsDashboard() {
  const { user, profile } = useAuth();
  const apiClient = useApiClient();

  const [activeTab, setActiveTab] = useState('generator');

  // Estados para WhatsApp Business API
  const [isWhatsAppConfigOpen, setIsWhatsAppConfigOpen] = useState(false);
  const [whatsAppConfig, setWhatsAppConfig] = useState<WhatsAppApiConfig>(() => WhatsAppApiService.getConfig());
  const [isSendingWhatsAppApi, setIsSendingWhatsAppApi] = useState(false);

  // Directorio de Copropietarios (CRUD)
  const [registeredDebtors, setRegisteredDebtors] = useState<DebtorRecord[]>([]);
  const [isLoadingDebtors, setIsLoadingDebtors] = useState(false);
  const [debtorSearchQuery, setDebtorSearchQuery] = useState('');
  const [isDebtorDialogOpen, setIsDebtorDialogOpen] = useState(false);
  const [editingDebtor, setEditingDebtor] = useState<DebtorRecord | null>(null);
  const [selectedDebtorSource, setSelectedDebtorSource] = useState<DebtorRecord | null>(null);
  const [allowOverrideFields, setAllowOverrideFields] = useState(false);

  // Formulario modal de Deudor (Crear/Editar)
  const [debtorModalForm, setDebtorModalForm] = useState<DebtorRecord>({
    copropiedad_nombre: '',
    direccion: '',
    unidad_privada: '',
    propietario_nombre: '',
    propietario_id: '',
    telefono: '',
    email: '',
    saldo_capital: 0,
    saldo_intereses: 0,
    dias_mora: 30,
    periodos_adeudados: [],
    etapa_cobro: 'prejuridico',
    notas: ''
  });

  // Formulario de Generación Individual
  const [formData, setFormData] = useState({
    copropiedad_nombre: 'Conjunto Residencial Torres del Parque',
    direccion: '',
    copropiedad_nit: '900.852.147-3',
    administrador_nombre: 'Administración de la Copropiedad',
    propietario_nombre: '',
    propietario_id: '',
    unidad_privada: '',
    email: '',
    telefono: '',
    saldo_capital: '',
    saldo_intereses: '',
    dias_mora: '30',
    periodos_adeudados: 'Últimos 2 meses de administración',
    etapa_cobro: 'prejuridico',
    plazo_dias_respuesta: '5',
    cuenta_bancaria_info: 'Cuenta Corriente Bancolombia No. 123-456789-00 a nombre de la Copropiedad'
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedResult, setGeneratedResult] = useState<DemandLetterResult | null>(null);
  const [letterViewMode, setLetterViewMode] = useState<'preview' | 'edit'>('preview');

  // Asegura que los enlaces de pasarela de pago siempre apunten al dominio de producción público
  const ensureProductionPaymentLink = (url?: string): string => {
    if (!url) return '';
    if (url.includes('localhost:8001') || url.includes('127.0.0.1:8001')) {
      return url.replace(/^https?:\/\/(localhost|127\.0\.0\.1):8001/, 'https://api.miasistentelegalia.com');
    }
    return url;
  };

  // Formateador dinámico para el texto de WhatsApp (*negrita* y enlaces cortos)
  const renderFormattedWhatsAppText = (rawText: string) => {
    if (!rawText) return null;
    return rawText.split('\n').map((line, idx) => {
      const parts = line.split(/(\*[^\*]+\*)/g);
      return (
        <div key={idx} className={line.trim() === '' ? 'h-2' : 'min-h-[1.25rem] leading-relaxed'}>
          {parts.map((part, pIdx) => {
            if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
              return (
                <strong key={pIdx} className="font-bold text-emerald-950 dark:text-emerald-100">
                  {part.slice(1, -1)}
                </strong>
              );
            }
            if (part.includes('http://') || part.includes('https://')) {
              const urlParts = part.split(/(https?:\/\/[^\s]+)/g);
              return (
                <span key={pIdx}>
                  {urlParts.map((sub, sIdx) => {
                    if (sub.startsWith('http')) {
                      const displayUrl = ensureProductionPaymentLink(sub);
                      return (
                        <a
                          key={sIdx}
                          href={displayUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 font-mono text-emerald-800 dark:text-emerald-200 underline font-semibold bg-emerald-200/60 dark:bg-emerald-900/50 px-1.5 py-0.5 rounded text-[11px] hover:bg-emerald-300 transition-colors mx-0.5"
                        >
                          <ExternalLink className="h-3 w-3 inline" />
                          {displayUrl}
                        </a>
                      );
                    }
                    return sub;
                  })}
                </span>
              );
            }
            return part;
          })}
        </div>
      );
    });
  };

  // Estados de envío individual
  const [isSending, setIsSending] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);

  // Logs y Trazabilidad
  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);

  // Configuración de Periodicidad
  const [settings, setSettings] = useState<CopropiedadSettings>({
    copropiedad_id: 'default',
    nombre_copropiedad: 'Conjunto Residencial Torres del Parque',
    nit: '900.852.147-3',
    administrador_nombre: 'Administración PH',
    email_contacto: 'administracion@torresdelparque.com',
    telefono_contacto: '3001234567',
    cuenta_bancaria_info: 'Bancolombia Cuenta Corriente No. 123-456789-00',
    dia_corte_mes: 10,
    frecuencia_notificacion: 'quincenal',
    umbrales_dias_mora: [5, 30, 60, 90],
    canales_habilitados: ['email', 'whatsapp'],
    respetar_ley_2300: true,
    tasa_interes_tipo: 'maxima_legal'
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  // Estados para Carga Masiva Excel
  const [importedDebtors, setImportedDebtors] = useState<DebtorRecord[]>([]);
  const [batchSummary, setBatchSummary] = useState<BatchSummary | null>(null);
  const [selectedUnits, setSelectedUnits] = useState<Set<string>>(new Set());
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [isBatchSending, setIsBatchSending] = useState(false);
  const [batchChannelOption, setBatchChannelOption] = useState<'both' | 'whatsapp' | 'email'>('both');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cargar configuración, deudores y logs iniciales
  useEffect(() => {
    loadSettings();
    loadDebtors();
    loadLogs();
  }, []);

  const loadSettings = async () => {
    try {
      const res: any = await (apiClient as any).get('/api/v1/demand-letter/settings');
      const data = res?.data !== undefined ? res.data : res;
      if (data && (data.nombre_copropiedad || data.copropiedad_id)) {
        setSettings(prev => ({ ...prev, ...data }));
        setFormData(prev => ({
          ...prev,
          copropiedad_nombre: data.nombre_copropiedad || prev.copropiedad_nombre,
          copropiedad_nit: data.nit || prev.copropiedad_nit,
          administrador_nombre: data.administrador_nombre || prev.administrador_nombre,
          cuenta_bancaria_info: data.cuenta_bancaria_info || prev.cuenta_bancaria_info
        }));
      }
    } catch (err) {
      console.warn('Error cargando configuración inicial de cartera:', err);
    }
  };

  const loadDebtors = async () => {
    setIsLoadingDebtors(true);
    try {
      const copropiedadId = settings.copropiedad_id || 'default';
      const res: any = await (apiClient as any).get(`/api/v1/demand-letter/debtors?copropiedad_id=${encodeURIComponent(copropiedadId)}`);
      const data = res?.data !== undefined ? res.data : res;
      if (Array.isArray(data)) {
        setRegisteredDebtors(data.map((d: DebtorRecord) => ({
          ...d,
          payment_link: ensureProductionPaymentLink(d.payment_link)
        })));
      }
    } catch (err) {
      console.warn('Error cargando directorio de deudores:', err);
    } finally {
      setIsLoadingDebtors(false);
    }
  };

  const loadLogs = async () => {
    setIsLoadingLogs(true);
    try {
      const res: any = await (apiClient as any).get('/api/v1/demand-letter/logs?limit=50');
      const data = res?.data !== undefined ? res.data : res;
      if (Array.isArray(data)) {
        setLogs(data.map((l: NotificationLog) => ({
          ...l,
          payment_link: ensureProductionPaymentLink(l.payment_link),
          direct_link: ensureProductionPaymentLink(l.direct_link)
        })));
      }
    } catch (err) {
      console.warn('Error cargando logs de cobranza:', err);
    } finally {
      setIsLoadingLogs(false);
    }
  };

  // ── SELECCIÓN Y AUTOCOMPLETADO DESDE EL DROPDOWN EN COBRO INDIVIDUAL ──
  const handleSelectDebtorFromDropdown = (debtorKey: string) => {
    if (!debtorKey || debtorKey === '__manual__') {
      setSelectedDebtorSource(null);
      setAllowOverrideFields(true);
      return;
    }

    const selected = registeredDebtors.find(d => (d.id === debtorKey || d.unidad_privada === debtorKey));
    if (!selected) {
      setSelectedDebtorSource(null);
      setAllowOverrideFields(true);
      return;
    }

    setSelectedDebtorSource(selected);
    setAllowOverrideFields(false);

    setFormData(prev => ({
      ...prev,
      copropiedad_nombre: selected.copropiedad_nombre || prev.copropiedad_nombre,
      direccion: selected.direccion || '',
      unidad_privada: selected.unidad_privada,
      propietario_nombre: selected.propietario_nombre,
      propietario_id: selected.propietario_id || '',
      telefono: selected.telefono || '',
      email: selected.email || '',
      saldo_capital: String(selected.saldo_capital || ''),
      saldo_intereses: String(selected.saldo_intereses || ''),
      dias_mora: String(selected.dias_mora || 30),
      periodos_adeudados: selected.periodos_adeudados && selected.periodos_adeudados.length > 0
        ? selected.periodos_adeudados.join(', ')
        : prev.periodos_adeudados,
      etapa_cobro: selected.etapa_cobro || prev.etapa_cobro
    }));

    toast.success(`Datos de ${selected.unidad_privada} cargados desde Directorio PH.`);
  };

  // ── GESTIÓN CRUD DE DEUDORES (MODAL) ──
  const handleOpenCreateDebtorModal = () => {
    setEditingDebtor(null);
    setDebtorModalForm({
      copropiedad_nombre: settings.nombre_copropiedad || formData.copropiedad_nombre || '',
      direccion: '',
      unidad_privada: '',
      propietario_nombre: '',
      propietario_id: '',
      telefono: '',
      email: '',
      saldo_capital: 0,
      saldo_intereses: 0,
      dias_mora: 30,
      periodos_adeudados: [],
      etapa_cobro: 'prejuridico',
      notas: ''
    });
    setIsDebtorDialogOpen(true);
  };

  const handleOpenEditDebtorModal = (debtor: DebtorRecord) => {
    setEditingDebtor(debtor);
    setDebtorModalForm({
      id: debtor.id,
      copropiedad_id: debtor.copropiedad_id || 'default',
      copropiedad_nombre: debtor.copropiedad_nombre || settings.nombre_copropiedad || '',
      direccion: debtor.direccion || '',
      unidad_privada: debtor.unidad_privada,
      propietario_nombre: debtor.propietario_nombre,
      propietario_id: debtor.propietario_id || '',
      telefono: debtor.telefono || '',
      email: debtor.email || '',
      saldo_capital: debtor.saldo_capital || 0,
      saldo_intereses: debtor.saldo_intereses || 0,
      dias_mora: debtor.dias_mora || 0,
      periodos_adeudados: debtor.periodos_adeudados || [],
      etapa_cobro: debtor.etapa_cobro || 'prejuridico',
      notas: debtor.notas || ''
    });
    setIsDebtorDialogOpen(true);
  };

  const handleSaveDebtorModal = async () => {
    if (!debtorModalForm.unidad_privada.trim() || !debtorModalForm.propietario_nombre.trim()) {
      toast.error('La Unidad Privada y el Nombre del Propietario son obligatorios.');
      return;
    }

    try {
      const payload = {
        ...debtorModalForm,
        copropiedad_id: settings.copropiedad_id || 'default',
        saldo_capital: parseFloat(String(debtorModalForm.saldo_capital)) || 0,
        saldo_intereses: parseFloat(String(debtorModalForm.saldo_intereses)) || 0,
        dias_mora: parseInt(String(debtorModalForm.dias_mora), 10) || 0
      };

      await (apiClient as any).post('/api/v1/demand-letter/debtors', payload);
      toast.success(editingDebtor ? 'Inmueble actualizado exitosamente.' : 'Inmueble registrado en el directorio.');
      setIsDebtorDialogOpen(false);
      loadDebtors();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Error guardando en el directorio.');
    }
  };

  const handleDeleteDebtor = async (debtor: DebtorRecord) => {
    if (!window.confirm(`¿Está seguro de eliminar ${debtor.unidad_privada} (${debtor.propietario_nombre}) del directorio?`)) {
      return;
    }

    try {
      const debtorId = debtor.id || debtor.unidad_privada;
      await (apiClient as any).delete(`/api/v1/demand-letter/debtors/${encodeURIComponent(debtorId)}?copropiedad_id=${encodeURIComponent(settings.copropiedad_id || 'default')}`);
      toast.success(`Inmueble ${debtor.unidad_privada} eliminado.`);
      loadDebtors();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Error eliminando inmueble.');
    }
  };

  const handleQuickChargeDebtor = (debtor: DebtorRecord) => {
    handleSelectDebtorFromDropdown(debtor.id || debtor.unidad_privada);
    setActiveTab('generator');
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.propietario_nombre.trim() || !formData.unidad_privada.trim() || !formData.saldo_capital) {
      toast.error('Complete los datos obligatorios: Nombre del propietario, Unidad privada y Saldo capital.');
      return;
    }

    setIsGenerating(true);
    try {
      const payload = {
        copropiedad_nombre: formData.copropiedad_nombre,
        copropiedad_nit: formData.copropiedad_nit,
        administrador_nombre: formData.administrador_nombre,
        propietario_nombre: formData.propietario_nombre,
        propietario_id: formData.propietario_id,
        unidad_privada: formData.unidad_privada,
        email: formData.email,
        telefono: formData.telefono,
        saldo_capital: parseFloat(formData.saldo_capital) || 0,
        saldo_intereses: parseFloat(formData.saldo_intereses) || 0,
        dias_mora: parseInt(formData.dias_mora, 10) || 30,
        periodos_adeudados: formData.periodos_adeudados ? [formData.periodos_adeudados] : [],
        etapa_cobro: formData.etapa_cobro,
        plazo_dias_respuesta: parseInt(formData.plazo_dias_respuesta, 10) || 5,
        cuenta_bancaria_info: formData.cuenta_bancaria_info
      };

      const res: any = await (apiClient as any).post('/api/v1/demand-letter/generate', payload);
      const rawData = res?.data !== undefined ? res.data : res;
      const data: DemandLetterResult = {
        ...rawData,
        payment_link: ensureProductionPaymentLink(rawData.payment_link),
        whatsapp_message: rawData.whatsapp_message
          ? rawData.whatsapp_message.replace(/https?:\/\/(localhost|127\.0\.0\.1):8001/g, 'https://api.miasistentelegalia.com')
          : rawData.whatsapp_message
      };
      setGeneratedResult(data);
      toast.success('Requerimiento de cobro generado exitosamente con IA.');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail?.message || err?.message || 'Error al generar requerimiento.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSendNotification = async (channel: 'email' | 'whatsapp' | 'both' = 'both') => {
    if (!generatedResult) return;

    if (!formData.email && !formData.telefono) {
      toast.error('Por favor ingrese al menos un correo electrónico o teléfono celular.');
      return;
    }

    const channelsToSend: string[] = [];
    if (formData.email) channelsToSend.push('email');
    if (formData.telefono) channelsToSend.push('whatsapp');

    setIsSending(true);
    try {
      const payload = {
        copropiedad_id: settings.copropiedad_id || 'default',
        copropiedad_nombre: formData.copropiedad_nombre,
        direccion: formData.direccion || '',
        propietario_nombre: formData.propietario_nombre,
        propietario_id: formData.propietario_id,
        unidad_privada: formData.unidad_privada,
        email: formData.email,
        telefono: formData.telefono,
        canales: channelsToSend,
        etapa_cobro: formData.etapa_cobro,
        saldo_capital: parseFloat(formData.saldo_capital) || 0,
        saldo_intereses: parseFloat(formData.saldo_intereses) || 0,
        dias_mora: parseInt(formData.dias_mora, 10) || 30,
        periodos_adeudados: formData.periodos_adeudados ? [formData.periodos_adeudados] : [],
        carta_texto_completo: generatedResult.letter.content,
        whatsapp_mensaje: generatedResult.whatsapp_message,
        payment_link: ensureProductionPaymentLink(generatedResult.payment_link)
      };

      const res: any = await (apiClient as any).post('/api/v1/demand-letter/send-notification', payload);
      const data = res?.data !== undefined ? res.data : res;

      const emailRes = data?.resultados_canales?.find((c: any) => c.canal === 'email');
      const waRes = data?.resultados_canales?.find((c: any) => c.canal === 'whatsapp');

      if (emailRes?.estado === 'sent') {
        toast.success(`Notificación enviada por correo a ${formData.email} y registrada en bitácora.`);
      } else if (waRes?.estado === 'sent') {
        toast.success(`Notificación enviada por WhatsApp API a ${formData.telefono} y registrada en bitácora.`);
      } else {
        toast.success('Notificación registrada exitosamente en la bitácora de trazabilidad.');
      }

      loadLogs();
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Error despachando notificación.');
    } finally {
      setIsSending(false);
    }
  };

  const handleDirectWhatsAppClick = () => {
    if (!formData.telefono) {
      toast.error('Ingrese un número de teléfono móvil para WhatsApp.');
      return;
    }
    const cleanPhone = formData.telefono.replace(/\D/g, '');
    const phoneWithCountry = cleanPhone.startsWith('57') ? cleanPhone : `57${cleanPhone}`;
    const text = encodeURIComponent(generatedResult?.whatsapp_message || '');
    window.open(`https://wa.me/${phoneWithCountry}?text=${text}`, '_blank');
  };

  // ── DESPACHO DIRECTO VÍA WHATSAPP BUSINESS API (META CLOUD / TWILIO) ──
  const handleSendWhatsAppApiDirect = async (overridePhone?: string, overrideMsg?: string) => {
    const phoneToUse = overridePhone || formData.telefono;
    const msgToUse = overrideMsg || generatedResult?.whatsapp_message;

    if (!phoneToUse) {
      toast.error('Por favor digite un número de teléfono móvil para WhatsApp.');
      return;
    }
    if (!msgToUse) {
      toast.error('Genere primero el requerimiento de cobro para preparar el mensaje.');
      return;
    }

    const isClientApiConfigured = (
      (whatsAppConfig.provider === 'meta' && Boolean(whatsAppConfig.meta_phone_number_id && whatsAppConfig.meta_access_token)) ||
      (whatsAppConfig.provider === 'twilio' && Boolean(whatsAppConfig.twilio_account_sid && whatsAppConfig.twilio_auth_token))
    );

    setIsSendingWhatsAppApi(true);
    try {
      // 1. Si está configurado en el cliente (Meta Cloud Oficial o Twilio)
      if (isClientApiConfigured) {
        const res = await WhatsAppApiService.sendMessage(phoneToUse, msgToUse);

        if (res.success) {
          toast.success(`¡Mensaje entregado exitosamente por WhatsApp Business API (${res.provider === 'meta' ? 'Meta Cloud Oficial' : 'Twilio'})! ID: ${res.messageId || 'OK'}`);

          // Registrar en bitácora del backend para trazabilidad jurídica
          try {
            await (apiClient as any).post('/api/v1/demand-letter/send-notification', {
              copropiedad_id: settings.copropiedad_id || 'default',
              copropiedad_nombre: formData.copropiedad_nombre,
              direccion: formData.direccion || '',
              propietario_nombre: formData.propietario_nombre,
              propietario_id: formData.propietario_id,
              unidad_privada: formData.unidad_privada,
              email: formData.email,
              telefono: phoneToUse,
              canales: ['whatsapp'],
              etapa_cobro: formData.etapa_cobro,
              saldo_capital: parseFloat(formData.saldo_capital) || 0,
              saldo_intereses: parseFloat(formData.saldo_intereses) || 0,
              dias_mora: parseInt(formData.dias_mora, 10) || 30,
              periodos_adeudados: formData.periodos_adeudados ? [formData.periodos_adeudados] : [],
              carta_texto_completo: generatedResult?.letter?.content || msgToUse,
              whatsapp_mensaje: msgToUse,
              payment_link: ensureProductionPaymentLink(generatedResult?.payment_link)
            });
            loadLogs();
          } catch (logErr) {
            console.warn('Registro de bitácora backend:', logErr);
          }
          return;
        } else {
          toast.error(`Error en API WhatsApp: ${res.error || 'Revise sus credenciales'}`);
          return;
        }
      }

      // 2. Si no está configurado en el cliente, intentar mediante el backend
      const resBackend: any = await (apiClient as any).post('/api/v1/demand-letter/send-notification', {
        copropiedad_id: settings.copropiedad_id || 'default',
        copropiedad_nombre: formData.copropiedad_nombre,
        direccion: formData.direccion || '',
        propietario_nombre: formData.propietario_nombre,
        propietario_id: formData.propietario_id,
        unidad_privada: formData.unidad_privada,
        email: formData.email,
        telefono: phoneToUse,
        canales: ['whatsapp'],
        etapa_cobro: formData.etapa_cobro,
        saldo_capital: parseFloat(formData.saldo_capital) || 0,
        saldo_intereses: parseFloat(formData.saldo_intereses) || 0,
        dias_mora: parseInt(formData.dias_mora, 10) || 30,
        periodos_adeudados: formData.periodos_adeudados ? [formData.periodos_adeudados] : [],
        carta_texto_completo: generatedResult?.letter?.content || msgToUse,
        whatsapp_mensaje: msgToUse,
        payment_link: ensureProductionPaymentLink(generatedResult?.payment_link)
      });
      const dataBackend = resBackend?.data !== undefined ? resBackend.data : resBackend;
      const waBackendResult = dataBackend?.resultados_canales?.find((c: any) => c.canal === 'whatsapp');

      if (waBackendResult?.estado === 'sent') {
        toast.success(`¡Mensaje entregado exitosamente por la API de WhatsApp del servidor! ID: ${waBackendResult.external_id || 'OK'}`);
        loadLogs();
      } else {
        // La API no está configurada ni en frontend ni en backend (no abrir WhatsApp Web engañosamente)
        toast.warning(
          'La API de WhatsApp no está configurada para envíos automáticos en segundo plano. Configura las credenciales de Meta Cloud o usa "WhatsApp Web" para enviar manualmente.',
          {
            duration: 6000,
            action: {
              label: 'Configurar API',
              onClick: () => setIsWhatsAppConfigOpen(true)
            }
          }
        );
        setIsWhatsAppConfigOpen(true);
      }
    } catch (err: any) {
      toast.error(err.message || 'Error al conectar con la API de WhatsApp.');
    } finally {
      setIsSendingWhatsAppApi(false);
    }
  };

  const handleSaveSettings = async () => {
    setIsSavingSettings(true);
    try {
      await (apiClient as any).post('/api/v1/demand-letter/settings', settings);
      toast.success('Parámetros de cobranza y periodicidad guardados exitosamente.');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Error guardando configuración.');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSaveSettingsFromHub = async (newSettings: any) => {
    setSettings(newSettings);
    setIsSavingSettings(true);
    try {
      await (apiClient as any).post('/api/v1/demand-letter/settings', newSettings);
      toast.success('Parámetros de cobranza y periodicidad sincronizados.');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Error guardando configuración.');
      throw err;
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSchedulerTriggerBatchSend = async (candidates: any[]) => {
    const itemsToSend = candidates.map(u => ({
      copropiedad_id: settings.copropiedad_id || 'default',
      copropiedad_nombre: settings.nombre_copropiedad || formData.copropiedad_nombre || 'Copropiedad',
      unidad_privada: u.unidad_privada,
      propietario_nombre: u.propietario_nombre,
      propietario_id: u.propietario_id || '',
      email: u.email || '',
      telefono: u.telefono || '',
      saldo_capital: u.saldo_capital || 0,
      saldo_intereses: u.saldo_intereses || 0,
      dias_mora: u.dias_mora || 0,
      periodos_adeudados: u.periodos_adeudados && u.periodos_adeudados.length > 0 ? u.periodos_adeudados : [`Mora de ${u.dias_mora} días`],
      etapa_cobro: u.etapa_cobro || (u.dias_mora > 60 ? 'prejudicial_aviso_demanda' : u.dias_mora >= 31 ? 'prejuridico' : 'preventivo'),
      canales: ['whatsapp', 'email'],
      payment_link: ensureProductionPaymentLink(u.payment_link)
    }));

    setIsBatchSending(true);
    try {
      const res: any = await (apiClient as any).post('/api/v1/demand-letter/batch-send', {
        items: itemsToSend,
        canales: ['whatsapp', 'email']
      });
      const data = res?.data !== undefined ? res.data : res;
      toast.success(`Campaña de Orquestador Despachada: ${data.total_despachados || itemsToSend.length} requerimientos enviados.`);
      loadLogs();
      setActiveTab('logs');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Error al ejecutar despacho programado.');
      throw err;
    } finally {
      setIsBatchSending(false);
    }
  };

  // ── DESCARGA DE TÍTULO EJECUTIVO EN PDF (ART. 48 LEY 675) ──
  const handleDownloadTituloPDF = async (debtor?: DebtorRecord) => {
    setIsDownloadingPdf(true);
    try {
      const token = await getAccessToken();
      const payload = debtor ? {
        copropiedad_nombre: settings.nombre_copropiedad || formData.copropiedad_nombre,
        copropiedad_nit: settings.nit || formData.copropiedad_nit,
        administrador_nombre: settings.administrador_nombre || formData.administrador_nombre,
        unidad_privada: debtor.unidad_privada,
        propietario_nombre: debtor.propietario_nombre,
        propietario_id: debtor.propietario_id || '',
        saldo_capital: debtor.saldo_capital,
        saldo_intereses: debtor.saldo_intereses,
        saldo_total: debtor.saldo_total || (debtor.saldo_capital + debtor.saldo_intereses),
        dias_mora: debtor.dias_mora,
        periodos_adeudados: debtor.periodos_adeudados && debtor.periodos_adeudados.length > 0 ? debtor.periodos_adeudados : [`Mora de ${debtor.dias_mora} días`],
        cuenta_bancaria_info: settings.cuenta_bancaria_info || formData.cuenta_bancaria_info
      } : {
        copropiedad_nombre: formData.copropiedad_nombre,
        copropiedad_nit: formData.copropiedad_nit,
        administrador_nombre: formData.administrador_nombre,
        unidad_privada: formData.unidad_privada,
        propietario_nombre: formData.propietario_nombre,
        propietario_id: formData.propietario_id,
        saldo_capital: parseFloat(formData.saldo_capital) || 0,
        saldo_intereses: parseFloat(formData.saldo_intereses) || 0,
        saldo_total: (parseFloat(formData.saldo_capital) || 0) + (parseFloat(formData.saldo_intereses) || 0),
        dias_mora: parseInt(formData.dias_mora, 10) || 30,
        periodos_adeudados: formData.periodos_adeudados ? [formData.periodos_adeudados] : [],
        cuenta_bancaria_info: formData.cuenta_bancaria_info
      };

      const res = await fetch(`${API_BASE_URL}/api/v1/demand-letter/export-titulo-ejecutivo-pdf`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error al generar Título Ejecutivo en PDF');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cleanUnit = (payload.unidad_privada || 'Inmueble').replace(/\s+/g, '_');
      a.download = `Titulo_Ejecutivo_Art48_${cleanUnit}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Título Ejecutivo PDF generado para ${payload.unidad_privada}`);
    } catch (err: any) {
      toast.error(err.message || 'Error al descargar Título Ejecutivo');
    } finally {
      setIsDownloadingPdf(false);
    }
  };

  // ── MANEJO DE IMPORTACIÓN MASIVA EXCEL ──
  const handleDownloadTemplate = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch(`${API_BASE_URL}/api/v1/demand-letter/template-excel`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'Plantilla_Cartera_Propiedad_Horizontal.xlsx';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Plantilla Excel modelo descargada.');
        return;
      }
    } catch (err: any) {
      console.warn('Descarga por API falló, recurriendo a archivo local modelo.xlsx:', err);
    }

    // Fallback garantizado al archivo estático modelo.xlsx
    try {
      const a = document.createElement('a');
      a.href = '/modelo.xlsx';
      a.download = 'Plantilla_Cartera_Propiedad_Horizontal.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Plantilla Excel modelo descargada.');
    } catch (e: any) {
      toast.error('No se pudo descargar la plantilla Excel.');
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    setIsUploadingFile(true);
    try {
      const token = await getAccessToken();
      const res = await fetch(`${API_BASE_URL}/api/v1/demand-letter/import-excel?copropiedad_id=${encodeURIComponent(settings.copropiedad_id || 'default')}`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formDataUpload
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error procesando archivo Excel');
      }

      const data = await res.json();
      const cleanedUnits = (data.unidades || []).map((u: DebtorRecord) => ({
        ...u,
        payment_link: ensureProductionPaymentLink(u.payment_link)
      }));
      setImportedDebtors(cleanedUnits);
      setBatchSummary({
        total_unidades: data.total_unidades || 0,
        total_deuda: data.total_deuda || 0,
        semaforo: data.conteo_semaforo || { verde: 0, amarillo: 0, rojo: 0 }
      });

      const allUnits = new Set<string>(cleanedUnits.map((u: DebtorRecord) => u.unidad_privada));
      setSelectedUnits(allUnits);

      toast.success(`Planilla importada y sincronizada: ${data.total_unidades} unidades registradas en directorio.`);
      loadDebtors();
    } catch (err: any) {
      toast.error(err.message || 'Error al importar archivo de cartera.');
    } finally {
      setIsUploadingFile(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleSelectAll = () => {
    if (selectedUnits.size === importedDebtors.length) {
      setSelectedUnits(new Set());
    } else {
      setSelectedUnits(new Set(importedDebtors.map(u => u.unidad_privada)));
    }
  };

  const toggleSelectUnit = (unidad: string) => {
    const next = new Set(selectedUnits);
    if (next.has(unidad)) {
      next.delete(unidad);
    } else {
      next.add(unidad);
    }
    setSelectedUnits(next);
  };

  const handleBatchSend = async () => {
    if (selectedUnits.size === 0) {
      toast.error('Seleccione al menos una unidad para despachar la campaña.');
      return;
    }

    const itemsToSend = importedDebtors
      .filter(u => selectedUnits.has(u.unidad_privada))
      .map(u => ({
        copropiedad_id: settings.copropiedad_id || 'default',
        copropiedad_nombre: u.copropiedad_nombre || settings.nombre_copropiedad || 'Copropiedad',
        direccion: u.direccion || '',
        unidad_privada: u.unidad_privada,
        propietario_nombre: u.propietario_nombre,
        propietario_id: u.propietario_id || '',
        email: u.email || '',
        telefono: u.telefono || '',
        saldo_capital: u.saldo_capital || 0,
        saldo_intereses: u.saldo_intereses || 0,
        dias_mora: u.dias_mora || 0,
        periodos_adeudados: [`Mora de ${u.dias_mora} días`],
        etapa_cobro: u.etapa_cobro,
        canales: batchChannelOption === 'both' ? ['whatsapp', 'email'] : [batchChannelOption],
        payment_link: ensureProductionPaymentLink(u.payment_link)
      }));

    setIsBatchSending(true);
    try {
      const res: any = await (apiClient as any).post('/api/v1/demand-letter/batch-send', {
        items: itemsToSend,
        canales: batchChannelOption === 'both' ? ['whatsapp', 'email'] : [batchChannelOption]
      });
      const data = res?.data !== undefined ? res.data : res;

      toast.success(`Campaña Masiva Despachada: ${data.total_despachados || itemsToSend.length} notificaciones procesadas.`);
      loadLogs();
      setActiveTab('logs');
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || err?.message || 'Error al ejecutar campaña masiva.');
    } finally {
      setIsBatchSending(false);
    }
  };

  // ── CÁLCULO DE DATOS PARA GRÁFICOS ANALÍTICOS (RECHARTS) ──
  const pieChartData = useMemo(() => {
    if (!batchSummary) return [];
    return [
      { name: 'Preventivo (<30d)', value: batchSummary.semaforo.verde, color: COLORS_SEMAFORO.verde },
      { name: 'Prejurídico (31-60d)', value: batchSummary.semaforo.amarillo, color: COLORS_SEMAFORO.amarillo },
      { name: 'Prejudicial (>60d)', value: batchSummary.semaforo.rojo, color: COLORS_SEMAFORO.rojo }
    ].filter(item => item.value > 0);
  }, [batchSummary]);

  const barChartTowerData = useMemo(() => {
    if (!importedDebtors.length) return [];
    const towerMap: Record<string, number> = {};
    importedDebtors.forEach(d => {
      const match = d.unidad_privada.match(/^(Torre\s*\w+|Bloque\s*\w+|T\d+|Interior\s*\w+)/i);
      const towerName = match ? match[0].toUpperCase() : 'ZONA GENERAL';
      const tot = d.saldo_total || (d.saldo_capital + d.saldo_intereses);
      towerMap[towerName] = (towerMap[towerName] || 0) + (tot || 0);
    });

    return Object.entries(towerMap).map(([torre, saldo]) => ({
      torre,
      saldo: Math.round(saldo)
    })).sort((a, b) => b.saldo - a.saldo).slice(0, 8);
  }, [importedDebtors]);

  const topDebtors = useMemo(() => {
    if (!importedDebtors.length) return [];
    const sorted = [...importedDebtors].sort((a, b) => {
      const totA = a.saldo_total || (a.saldo_capital + a.saldo_intereses);
      const totB = b.saldo_total || (b.saldo_capital + b.saldo_intereses);
      return totB - totA;
    });
    const totalDeuda = batchSummary?.total_deuda || 1;
    return sorted.slice(0, 5).map(d => {
      const tot = d.saldo_total || (d.saldo_capital + d.saldo_intereses);
      return {
        ...d,
        saldo_total: tot,
        porcentaje: ((tot / totalDeuda) * 100).toFixed(1)
      };
    });
  }, [importedDebtors, batchSummary]);

  // Filtrado de deudores en el directorio
  const filteredDebtors = useMemo(() => {
    if (!debtorSearchQuery.trim()) return registeredDebtors;
    const q = debtorSearchQuery.toLowerCase();
    return registeredDebtors.filter(d =>
      d.unidad_privada.toLowerCase().includes(q) ||
      d.propietario_nombre.toLowerCase().includes(q) ||
      (d.copropiedad_nombre && d.copropiedad_nombre.toLowerCase().includes(q)) ||
      (d.direccion && d.direccion.toLowerCase().includes(q)) ||
      (d.propietario_id && d.propietario_id.toLowerCase().includes(q))
    );
  }, [registeredDebtors, debtorSearchQuery]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copiado al portapapeles.');
  };

  const downloadText = (filename: string, text: string) => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <DashboardBreadcrumbs current="Recolección de Deudas (PH)" />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-100 text-orange-700 dark:bg-orange-950 dark:text-orange-400">
              <Building2 className="h-6 w-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold tracking-tight">
                  Recuperación de Cartera PH
                </h1>
                <Badge variant="secondary" className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950 dark:text-amber-300">
                  Admin Only
                </Badge>
                <Badge variant="outline" className="border-blue-200 text-blue-700 dark:text-blue-400">
                  Ley 675 de 2001
                </Badge>
              </div>
              <p className="text-muted-foreground text-sm mt-0.5">
                Directorio CRUD de copropietarios, autocompletado en cobro individual, Títulos Ejecutivos PDF y pagos PSE.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Botón y Estado de la API de WhatsApp */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsWhatsAppConfigOpen(true)}
            className={`gap-1.5 text-xs h-9 font-medium shadow-xs transition-colors ${whatsAppConfig.provider === 'meta' && whatsAppConfig.meta_access_token
                ? 'border-emerald-400 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'
                : whatsAppConfig.provider === 'twilio' && whatsAppConfig.twilio_auth_token
                  ? 'border-blue-400 bg-blue-50 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800'
                  : 'border-amber-300 bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800'
              }`}
          >
            <span className="relative flex h-2 w-2">
              <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${whatsAppConfig.provider !== 'direct_wame' && (whatsAppConfig.meta_access_token || whatsAppConfig.twilio_auth_token)
                  ? 'bg-emerald-400'
                  : 'bg-amber-400'
                }`}></span>
              <span className={`relative inline-flex rounded-full h-2 w-2 ${whatsAppConfig.provider !== 'direct_wame' && (whatsAppConfig.meta_access_token || whatsAppConfig.twilio_auth_token)
                  ? 'bg-emerald-500'
                  : 'bg-amber-500'
                }`}></span>
            </span>
            <MessageSquare className="h-3.5 w-3.5" />
            {whatsAppConfig.provider === 'meta' && whatsAppConfig.meta_access_token
              ? 'WhatsApp API: Meta Oficial'
              : whatsAppConfig.provider === 'twilio' && whatsAppConfig.twilio_auth_token
                ? 'WhatsApp API: Twilio Activo'
                : 'Configurar API WhatsApp'}
          </Button>

          <Button
            variant="outline"
            size="sm"
            onClick={() => { loadLogs(); loadDebtors(); }}
            disabled={isLoadingLogs || isLoadingDebtors}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoadingLogs || isLoadingDebtors ? 'animate-spin' : ''}`} />
            Sincronizar Datos
          </Button>
        </div>
      </div>

      {/* Navegación por Pestañas */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        {/* Vista Mobile: Selector desplegable compacto y barra deslizable horizontal (md:hidden) */}
        <div className="block md:hidden space-y-2">
          <div className="flex items-center gap-2">
            <Select value={activeTab} onValueChange={setActiveTab}>
              <SelectTrigger className="w-full h-10 bg-white dark:bg-slate-900 border-slate-200 text-xs font-semibold shadow-xs">
                <SelectValue placeholder="Seleccionar módulo..." />
              </SelectTrigger>
              <SelectContent className="max-h-80">
                <SelectItem value="generator">📄 Cobro Individual</SelectItem>
                <SelectItem value="directory">👥 Directorio PH</SelectItem>
                <SelectItem value="agreements">🤝 Acuerdos & Portal</SelectItem>
                <SelectItem value="litigation">⚖️ Cobro Jurídico</SelectItem>
                <SelectItem value="liquidador">🧮 Liquidador SFC (Usura)</SelectItem>
                <SelectItem value="bulk">📊 Carga Masiva Excel</SelectItem>
                <SelectItem value="logs">🕒 Bitácora & Control</SelectItem>
                <SelectItem value="settings">⚙️ Automatización Cron (Beat)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex overflow-x-auto no-scrollbar gap-1.5 pb-1">
            {[
              { id: "generator", label: "Cobro", icon: FileText },
              { id: "directory", label: "Directorio", icon: Users },
              { id: "agreements", label: "Acuerdos", icon: Handshake },
              { id: "litigation", label: "Litigios", icon: Gavel },
              { id: "liquidador", label: "Liquidador", icon: Calculator },
              { id: "bulk", label: "Excel", icon: FileSpreadsheet },
              { id: "logs", label: "Bitácora", icon: Clock },
              { id: "settings", label: "Cron", icon: Cpu },
            ].map((t) => {
              const Icon = t.icon;
              const isActive = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setActiveTab(t.id)}
                  className={`shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${isActive
                      ? "bg-orange-600 text-white font-bold shadow-xs"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                    }`}
                >
                  <Icon className="h-3 w-3" />
                  <span>{t.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Vista Desktop / Tablet (hidden md:grid) */}
        <TabsList className="hidden md:grid md:grid-cols-8 max-w-6xl">
          <TabsTrigger value="generator" className="gap-1.5 text-xs">
            <FileText className="h-3.5 w-3.5" />
            Cobro Individual
          </TabsTrigger>
          <TabsTrigger value="directory" className="gap-1.5 text-xs">
            <Users className="h-3.5 w-3.5" />
            Directorio PH
          </TabsTrigger>
          <TabsTrigger value="agreements" className="gap-1.5 text-xs">
            <Handshake className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            Acuerdos & Portal
          </TabsTrigger>
          <TabsTrigger value="litigation" className="gap-1.5 text-xs">
            <Gavel className="h-3.5 w-3.5 text-indigo-600 dark:text-indigo-400" />
            Cobro Jurídico
          </TabsTrigger>
          <TabsTrigger value="liquidador" className="gap-1.5 text-xs relative">
            <Calculator className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
            Liquidador SFC
            <span className="ml-1 px-1.5 py-0.2 rounded-full text-[9px] bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 font-bold">
              Usura
            </span>
          </TabsTrigger>
          <TabsTrigger value="bulk" className="gap-1.5 text-xs">
            <FileSpreadsheet className="h-3.5 w-3.5" />
            Carga Masiva Excel
          </TabsTrigger>
          <TabsTrigger value="logs" className="gap-1.5 text-xs">
            <Clock className="h-3.5 w-3.5" />
            Bitácora & Control
          </TabsTrigger>
          <TabsTrigger value="settings" className="gap-1.5 text-xs relative">
            <Cpu className="h-3.5 w-3.5 text-orange-600 dark:text-orange-400" />
            Automatización Cron
            <span className="ml-1 px-1.5 py-0.2 rounded-full text-[9px] bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300 font-bold">
              Beat
            </span>
          </TabsTrigger>
        </TabsList>

        {/* ── PESTAÑA 1: COBRO INDIVIDUAL (CON AUTOCOMPLETADO DROPDOWN) ── */}
        <TabsContent value="generator" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Formulario de Datos */}
            <Card className="lg:col-span-5 shadow-sm border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-orange-600" />
                    <span>Datos del Copropietario y Deuda</span>
                  </div>
                </CardTitle>
                <CardDescription className="text-xs">
                  Seleccione un inmueble registrado para autocompletar o digite la información manualmente.
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* ── CAJA DE AUTOCOMPLETADO RÁPIDO CON DROPDOWN ── */}
                <div className="p-3 rounded-lg border bg-gradient-to-r from-orange-50/70 to-amber-50/50 dark:from-orange-950/20 dark:to-amber-950/10 border-orange-200 dark:border-orange-900/40 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs font-semibold flex items-center gap-1.5 text-orange-900 dark:text-orange-300">
                      <UserCheck className="h-3.5 w-3.5 text-orange-600" />
                      Vincular Inmueble desde el Directorio PH
                    </Label>
                    <span className="text-[11px] font-mono text-muted-foreground">
                      {registeredDebtors.length} unidades registradas
                    </span>
                  </div>

                  <Select onValueChange={handleSelectDebtorFromDropdown}>
                    <SelectTrigger className="h-8 text-xs bg-white dark:bg-slate-900 shadow-sm border-orange-200 dark:border-orange-900/50">
                      <SelectValue placeholder="Seleccione un inmueble del directorio..." />
                    </SelectTrigger>
                    <SelectContent className="max-h-[260px]">
                      <SelectItem value="__manual__">
                        ✏️ Redacción Manual Libre (Sin vincular a Directorio)
                      </SelectItem>
                      {registeredDebtors.length === 0 ? (
                        <div className="p-2 text-xs text-muted-foreground text-center">
                          No hay deudores registrados aún. Agregue uno en la pestaña &apos;Directorio PH&apos; o importe un Excel.
                        </div>
                      ) : (
                        registeredDebtors.map(d => {
                          const tot = d.saldo_total || (d.saldo_capital + d.saldo_intereses);
                          return (
                            <SelectItem key={d.id || d.unidad_privada} value={d.id || d.unidad_privada}>
                              {d.unidad_privada} — {d.propietario_nombre} (${tot?.toLocaleString('es-CO')})
                            </SelectItem>
                          );
                        })
                      )}
                    </SelectContent>
                  </Select>

                  {/* Banner de Protección de Datos de la Ficha Maestra */}
                  {selectedDebtorSource ? (
                    <div className="p-2.5 rounded-md border border-sky-200 dark:border-sky-900/60 bg-sky-50/70 dark:bg-sky-950/30 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-2 mt-2">
                      <div className="flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-sky-600 dark:text-sky-400 shrink-0" />
                        <span className="text-[11px] text-sky-900 dark:text-sky-200">
                          Inmueble vinculado: <strong>{selectedDebtorSource.unidad_privada}</strong>. Los campos están protegidos para no alterar la ficha maestra.
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setAllowOverrideFields(!allowOverrideFields)}
                          className="h-6 text-[10px] px-2 text-sky-700 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/40"
                        >
                          {allowOverrideFields ? (
                            <>
                              <Lock className="h-3 w-3 mr-1" /> Bloquear
                            </>
                          ) : (
                            <>
                              <Edit className="h-3 w-3 mr-1" /> Modificar solo para este envío
                            </>
                          )}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleOpenEditDebtorModal(selectedDebtorSource)}
                          className="h-6 text-[10px] px-2 border-sky-300 text-sky-800 dark:text-sky-300 hover:bg-sky-100 dark:hover:bg-sky-900/40 font-medium"
                        >
                          <UserCheck className="h-3 w-3 mr-1" />
                          Editar Ficha Maestra
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 pt-0.5">
                      <Info className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      <span>
                        Este formulario es de emisión puntual. Los cambios hechos aquí solo aplican para la carta actual y no alteran el <strong>Directorio PH</strong>.
                      </span>
                    </div>
                  )}
                </div>

                <form onSubmit={handleGenerate} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Copropiedad / Edificio</Label>
                      <Input
                        value={formData.copropiedad_nombre}
                        onChange={e => setFormData({ ...formData, copropiedad_nombre: e.target.value })}
                        placeholder="Nombre del Conjunto"
                        className="text-xs h-9"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium">Unidad Privada (Torre / Apto)</Label>
                        {selectedDebtorSource && !allowOverrideFields && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1 font-mono">
                            <Lock className="h-3 w-3 text-muted-foreground" /> Ficha Protegida
                          </span>
                        )}
                      </div>
                      <Input
                        value={formData.unidad_privada}
                        onChange={e => setFormData({ ...formData, unidad_privada: e.target.value })}
                        placeholder="Ej: Torre 2 Apto 504"
                        className={`text-xs h-9 ${selectedDebtorSource && !allowOverrideFields ? 'bg-muted/40 cursor-not-allowed' : ''}`}
                        readOnly={Boolean(selectedDebtorSource && !allowOverrideFields)}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium">Nombre del Propietario</Label>
                        {selectedDebtorSource && !allowOverrideFields && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1 font-mono">
                            <Lock className="h-3 w-3 text-muted-foreground" /> Ficha Protegida
                          </span>
                        )}
                      </div>
                      <Input
                        value={formData.propietario_nombre}
                        onChange={e => setFormData({ ...formData, propietario_nombre: e.target.value })}
                        placeholder="Nombre completo"
                        className={`text-xs h-9 ${selectedDebtorSource && !allowOverrideFields ? 'bg-muted/40 cursor-not-allowed' : ''}`}
                        readOnly={Boolean(selectedDebtorSource && !allowOverrideFields)}
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium">Cédula o NIT</Label>
                        {selectedDebtorSource && !allowOverrideFields && (
                          <span className="text-[10px] text-muted-foreground flex items-center gap-1 font-mono">
                            <Lock className="h-3 w-3 text-muted-foreground" /> Ficha Protegida
                          </span>
                        )}
                      </div>
                      <Input
                        value={formData.propietario_id}
                        onChange={e => setFormData({ ...formData, propietario_id: e.target.value })}
                        placeholder="Número de documento"
                        className={`text-xs h-9 ${selectedDebtorSource && !allowOverrideFields ? 'bg-muted/40 cursor-not-allowed' : ''}`}
                        readOnly={Boolean(selectedDebtorSource && !allowOverrideFields)}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">WhatsApp / Móvil</Label>
                      <Input
                        value={formData.telefono}
                        onChange={e => setFormData({ ...formData, telefono: e.target.value })}
                        placeholder="3001234567"
                        className="text-xs h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Correo Electrónico</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                        placeholder="propietario@email.com"
                        className="text-xs h-9"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Saldo Capital ($)</Label>
                      <Input
                        type="number"
                        value={formData.saldo_capital}
                        onChange={e => setFormData({ ...formData, saldo_capital: e.target.value })}
                        placeholder="Ej: 1250000"
                        className="text-xs h-9"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-medium">Intereses ($)</Label>
                        <button
                          type="button"
                          onClick={() => {
                            setActiveTab('liquidador');
                            toast.info('Abriendo Liquidador Oficial con Tasas de Usura de la Superfinanciera.');
                          }}
                          className="text-[10px] text-emerald-600 dark:text-emerald-400 hover:underline flex items-center gap-0.5 font-semibold"
                        >
                          <Calculator className="h-2.5 w-2.5" /> Liquidar SFC
                        </button>
                      </div>
                      <Input
                        type="number"
                        value={formData.saldo_intereses}
                        onChange={e => setFormData({ ...formData, saldo_intereses: e.target.value })}
                        placeholder="Ej: 85000"
                        className="text-xs h-9"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Días de Mora</Label>
                      <Input
                        type="number"
                        value={formData.dias_mora}
                        onChange={e => setFormData({ ...formData, dias_mora: e.target.value })}
                        placeholder="30"
                        className="text-xs h-9"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Etapa de Cobranza</Label>
                      <Select
                        value={formData.etapa_cobro}
                        onValueChange={val => setFormData({ ...formData, etapa_cobro: val })}
                      >
                        <SelectTrigger className="h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="preventivo">Preventivo (1 a 30 días)</SelectItem>
                          <SelectItem value="prejuridico">Prejurídico Formal (31 a 60 días)</SelectItem>
                          <SelectItem value="prejudicial_aviso_demanda">Aviso Prejudicial / Demanda (&gt;60 días)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <Label className="text-xs font-medium">Plazo de Pago (Días)</Label>
                      <Input
                        type="number"
                        value={formData.plazo_dias_respuesta}
                        onChange={e => setFormData({ ...formData, plazo_dias_respuesta: e.target.value })}
                        className="text-xs h-9"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Meses / Conceptos Adeudados</Label>
                    <Input
                      value={formData.periodos_adeudados}
                      onChange={e => setFormData({ ...formData, periodos_adeudados: e.target.value })}
                      placeholder="Ej: Cuotas de Mayo, Junio y Julio"
                      className="text-xs h-9"
                    />
                  </div>

                  <div className="space-y-1">
                    <Label className="text-xs font-medium">Datos de Pago (Cuenta Bancaria)</Label>
                    <Textarea
                      value={formData.cuenta_bancaria_info}
                      onChange={e => setFormData({ ...formData, cuenta_bancaria_info: e.target.value })}
                      rows={2}
                      className="text-xs resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isGenerating}
                    className="w-full gap-2 bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700 text-white font-medium shadow"
                  >
                    {isGenerating ? (
                      <>
                        <RefreshCw className="h-4 w-4 animate-spin" />
                        Generando con IA Jurídica...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4" />
                        Generar Requerimiento de Cobro
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Panel de Resultados y Despacho */}
            <div className="lg:col-span-7 space-y-6">
              {generatedResult ? (
                <div className="space-y-6">
                  {/* Resumen Superior */}
                  <Card className="border-green-200 bg-green-50/50 dark:bg-green-950/20 shadow-sm">
                    <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600" />
                          <span className="font-semibold text-sm text-green-950 dark:text-green-300">
                            Requerimiento Preparado para {generatedResult.unidad_privada}
                          </span>
                        </div>
                        <p className="text-xs text-green-800 dark:text-green-400 mt-1">
                          Total a Exigir: <strong>${generatedResult.saldo_total?.toLocaleString('es-CO')} COP</strong> | Etapa: <strong>{generatedResult.etapa_cobro}</strong>
                        </p>
                      </div>

                      {/* Botones de Acción */}
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={isDownloadingPdf}
                          onClick={() => handleDownloadTituloPDF()}
                          className="border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-900 dark:text-rose-300 dark:hover:bg-rose-950 gap-1.5 h-8 text-xs font-medium"
                        >
                          <FileDown className={`h-3.5 w-3.5 ${isDownloadingPdf ? 'animate-spin' : ''}`} />
                          Título PDF (Art. 48)
                        </Button>

                        <Button
                          size="sm"
                          variant="default"
                          disabled={isSending}
                          onClick={() => handleSendNotification('both')}
                          title="Despachar formalmente la notificación y asentar en la bitácora legal"
                          className="bg-blue-600 hover:bg-blue-700 text-white gap-1.5 h-8 text-xs font-semibold shadow-xs"
                        >
                          <Send className="h-3.5 w-3.5" />
                          {isSending ? 'Despachando...' : 'Despachar y Registrar'}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>

                  {/* ── 1. PASARELA DE PAGO / ENLACE RECORTADO ── */}
                  {generatedResult.payment_link && (
                    <Card className="border-indigo-200 dark:border-indigo-900 bg-gradient-to-r from-indigo-50/70 via-white to-blue-50/70 dark:from-indigo-950/30 dark:via-slate-900 dark:to-blue-950/30 shadow-sm">
                      <CardHeader className="pb-2 flex flex-row items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-lg bg-indigo-600 text-white shadow-sm">
                            <CreditCard className="h-4 w-4" />
                          </div>
                          <div>
                            <CardTitle className="text-sm font-bold text-indigo-950 dark:text-indigo-200">
                              Pasarela Oficial de Pago (PSE / Tarjeta)
                            </CardTitle>
                            <CardDescription className="text-[11px] text-indigo-700/80 dark:text-indigo-300/80">
                              Enlace recortado seguro con firma de integridad Wompi
                            </CardDescription>
                          </div>
                        </div>
                        <Badge variant="outline" className="border-indigo-300 text-indigo-700 bg-white dark:bg-black text-[11px] font-semibold gap-1">
                          <ShieldCheck className="h-3 w-3 text-indigo-600" />
                          Recaudo Inmediato PSE
                        </Badge>
                      </CardHeader>
                      <CardContent className="space-y-3 pt-1">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                          <div className="relative flex-1">
                            <Input
                              readOnly
                              value={ensureProductionPaymentLink(generatedResult.payment_link)}
                              className="text-xs h-9 bg-white dark:bg-slate-900 font-mono text-indigo-950 dark:text-indigo-200 border-indigo-200 dark:border-indigo-800 pr-20 font-medium"
                            />
                            <span className="absolute right-2.5 top-2.5 text-[10px] font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-950/60 px-1.5 py-0.5 rounded border border-emerald-200">
                              Link Corto
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyToClipboard(ensureProductionPaymentLink(generatedResult.payment_link))}
                              className="h-9 text-xs gap-1.5 border-indigo-200 hover:bg-indigo-100 dark:border-indigo-800"
                            >
                              <Copy className="h-3.5 w-3.5 text-indigo-600" /> Copiar Link
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => window.open(ensureProductionPaymentLink(generatedResult.payment_link), '_blank')}
                              className="h-9 text-xs gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white shadow-sm"
                            >
                              <ExternalLink className="h-3.5 w-3.5" /> Abrir Pasarela
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setActiveTab('agreements');
                                toast.info('Abriendo simulador de acuerdos de pago para esta unidad.');
                              }}
                              className="h-9 text-xs gap-1.5 border-emerald-300 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950 font-medium"
                            >
                              <Handshake className="h-3.5 w-3.5 text-emerald-600" /> Pactar Acuerdo
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* ── 2. BURBUJA AUTÉNTICA DE WHATSAPP CON TEXTO PERSUASIVO ── */}
                  <Card className="border-emerald-300/80 dark:border-emerald-800 shadow-sm overflow-hidden">
                    <CardHeader className="pb-2.5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex flex-row items-center justify-between">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center font-bold text-xs text-white border border-white/30">
                          PH
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <CardTitle className="text-sm font-bold text-white leading-none">
                              {formData.copropiedad_nombre}
                            </CardTitle>
                          </div>
                          <p className="text-[10px] text-emerald-100 flex items-center gap-1 mt-0.5 font-medium">
                            <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-300 animate-pulse"></span>
                            Administración • En línea para WhatsApp
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(generatedResult.whatsapp_message)}
                          className="h-7 text-xs gap-1 bg-white/10 hover:bg-white/20 text-white border border-white/20"
                        >
                          <Copy className="h-3 w-3" /> Copiar
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleDirectWhatsAppClick}
                          className="h-7 text-xs gap-1 bg-white/15 hover:bg-white/25 text-white font-medium shadow-sm"
                        >
                          <MessageSquare className="h-3 w-3" /> Abrir WA Web
                        </Button>
                      </div>
                    </CardHeader>

                    <CardContent className="p-4 bg-[#EFEAE2] dark:bg-[#0b141a]">
                      {/* Burbuja Verde WhatsApp */}
                      <div className="relative bg-[#D9FDD3] dark:bg-[#005C4B] text-slate-900 dark:text-slate-100 p-4 rounded-2xl rounded-tl-none shadow-md max-w-full text-xs space-y-2 border border-emerald-300/60 dark:border-emerald-700/60">
                        {renderFormattedWhatsAppText(generatedResult.whatsapp_message)}

                        {/* Pie de burbuja con hora y doble check azul */}
                        <div className="pt-2 border-t border-emerald-400/30 flex items-center justify-between text-[10px] text-slate-500 dark:text-emerald-200">
                          <span className="font-medium text-emerald-800 dark:text-emerald-200">
                            Cobro Prejurídico Ley 675 / Ley 2300 de 2023
                          </span>
                          <span className="flex items-center gap-1 font-medium">
                            Hoy {new Date().toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' })}
                            <CheckCheck className="h-3.5 w-3.5 text-sky-500 inline stroke-[2.5]" />
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* ── 3. CARTA FORMAL DE COBRO CON MEMBRETE INSTITUCIONAL (PREVIEW/EDIT TOGGLE) ── */}
                  <Card className="shadow-md border border-slate-200 dark:border-slate-800 overflow-hidden">
                    <CardHeader className="pb-3 border-b bg-slate-50/80 dark:bg-slate-900/60 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-orange-600 text-white shadow-sm">
                          <FileText className="h-4 w-4" />
                        </div>
                        <div>
                          <CardTitle className="text-sm font-bold text-slate-900 dark:text-white">
                            Carta Formal de Cobro Prejurídico
                          </CardTitle>
                          <CardDescription className="text-[11px]">
                            Documento acreditativo de deuda con mérito ejecutivo pleno (Art. 48 Ley 675 / Art. 422 CGP)
                          </CardDescription>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5">
                        {/* Selector de modo Vista Previa / Edición */}
                        <div className="flex items-center bg-slate-200 dark:bg-slate-800 p-0.5 rounded-lg border text-xs mr-1">
                          <button
                            type="button"
                            onClick={() => setLetterViewMode('preview')}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all font-medium ${letterViewMode === 'preview'
                              ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs font-semibold'
                              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                              }`}
                          >
                            <Eye className="h-3 w-3" /> Membrete
                          </button>
                          <button
                            type="button"
                            onClick={() => setLetterViewMode('edit')}
                            className={`flex items-center gap-1 px-2.5 py-1 rounded-md transition-all font-medium ${letterViewMode === 'edit'
                              ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs font-semibold'
                              : 'text-slate-600 dark:text-slate-400 hover:text-slate-900'
                              }`}
                          >
                            <Code className="h-3 w-3" /> Editar Texto
                          </button>
                        </div>

                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyToClipboard(generatedResult.letter.content)}
                          className="h-7 text-xs gap-1 border-slate-300"
                        >
                          <Copy className="h-3 w-3" /> Copiar
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => downloadText(`Requerimiento_Cobro_${formData.unidad_privada.replace(/\s+/g, '_')}.txt`, generatedResult.letter.content)}
                          className="h-7 text-xs gap-1 border-slate-300"
                        >
                          <Download className="h-3 w-3" /> TXT
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleDownloadTituloPDF()}
                          disabled={isDownloadingPdf}
                          className="h-7 text-xs gap-1 bg-red-600 hover:bg-red-700 text-white font-medium shadow-sm"
                        >
                          <FileDown className="h-3 w-3" /> PDF Oficial
                        </Button>
                      </div>
                    </CardHeader>

                    <CardContent className="p-4 sm:p-6 bg-slate-100/50 dark:bg-slate-950/40">
                      {letterViewMode === 'preview' ? (
                        /* Hoja de Papel Membretada Institucional */
                        <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-xl p-6 sm:p-9 shadow-lg space-y-6 text-xs text-slate-800 dark:text-slate-200 max-w-3xl mx-auto font-sans leading-relaxed">

                          {/* Membrete Oficial */}
                          <div className="text-center pb-5 border-b-2 border-slate-900 dark:border-slate-600 space-y-1.5">
                            <span className="inline-block px-3 py-0.5 text-[10px] font-bold uppercase tracking-wider text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/60 rounded-full border border-orange-200 dark:border-orange-900">
                              Régimen de Propiedad Horizontal • Ley 675 de 2001
                            </span>
                            <h2 className="text-base sm:text-lg font-black tracking-wide text-slate-900 dark:text-white uppercase">
                              {formData.copropiedad_nombre}
                            </h2>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                              NIT: {formData.copropiedad_nit || 'Personería Jurídica Vigente'} &bull; Administración y Representación Legal
                            </p>
                          </div>

                          {/* Encabezado y Asunto */}
                          <div className="space-y-3 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-800">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between text-xs gap-1">
                              <div>
                                <span className="text-slate-500 font-medium">Destinatario: </span>
                                <strong className="text-slate-900 dark:text-white font-bold">{formData.propietario_nombre}</strong>
                                {formData.propietario_id && (
                                  <span className="text-slate-500"> (C.C./NIT: {formData.propietario_id})</span>
                                )}
                              </div>
                              <div>
                                <span className="text-slate-500 font-medium">Inmueble / Unidad: </span>
                                <strong className="text-orange-600 font-bold">{formData.unidad_privada}</strong>
                              </div>
                            </div>
                            <div className="text-xs pt-1 border-t border-slate-200 dark:border-slate-700">
                              <span className="text-slate-500 font-medium">Asunto: </span>
                              <strong className="text-slate-900 dark:text-white uppercase font-bold tracking-tight">
                                {formData.etapa_cobro === 'prejudicial_aviso_demanda'
                                  ? 'AVISO PREJUDICIAL - INICIO INMINENTE DE PROCESO EJECUTIVO Y MEDIDAS CAUTELARES'
                                  : 'REQUERIMIENTO FORMAL DE COBRO PREJURÍDICO Y LIQUIDACIÓN DE EXPENSAS COMUNES'}
                              </strong>
                            </div>
                          </div>

                          {/* Cuerpo del Requerimiento en Párrafos Formales (Renderizado Markdown Enriquecido) */}
                          <div className="prose prose-sm dark:prose-invert max-w-none text-slate-800 dark:text-slate-200 text-xs leading-relaxed text-justify space-y-2">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              components={{
                                h1: ({ node, ...props }) => <h1 className="text-xs font-bold uppercase tracking-wide text-slate-900 dark:text-white mt-4 mb-2 pb-1 border-b border-slate-200 dark:border-slate-800" {...props} />,
                                h2: ({ node, ...props }) => <h2 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white mt-3 mb-1.5" {...props} />,
                                h3: ({ node, ...props }) => <h3 className="text-xs font-semibold uppercase text-slate-800 dark:text-slate-200 mt-2 mb-1" {...props} />,
                                p: ({ node, ...props }) => <p className="mb-2 leading-relaxed text-justify" {...props} />,
                                strong: ({ node, ...props }) => <strong className="font-bold text-slate-900 dark:text-white" {...props} />,
                                ul: ({ node, ...props }) => <ul className="list-disc pl-5 my-2 space-y-1" {...props} />,
                                ol: ({ node, ...props }) => <ol className="list-decimal pl-5 my-2 space-y-1" {...props} />,
                                li: ({ node, ...props }) => <li className="leading-relaxed" {...props} />
                              }}
                            >
                              {generatedResult.letter.content}
                            </ReactMarkdown>
                          </div>

                          {/* Recuadro Destacado de Obligación y Mérito Ejecutivo */}
                          <div className="border-l-4 border-orange-500 bg-orange-50/60 dark:bg-orange-950/20 p-4 rounded-r-lg space-y-1.5 text-xs text-orange-950 dark:text-orange-200">
                            <div className="flex items-center gap-2 font-bold text-orange-900 dark:text-orange-300">
                              <ShieldAlert className="h-4 w-4 text-orange-600" />
                              MÉRITO EJECUTIVO PLENO (ARTÍCULO 48 LEY 675 DE 2001)
                            </div>
                            <p className="text-[11px] leading-relaxed text-orange-900/90 dark:text-orange-300/90">
                              La presente certificación de deuda expedida por la Administración presta mérito ejecutivo pleno conforme al
                              Artículo 422 del Código General del Proceso para cobro coactivo judicial, embargo del inmueble y reporte en centrales de riesgo (Ley 1266 de 2008).
                            </p>
                          </div>

                          {/* Firma Formal */}
                          <div className="pt-8 border-t border-slate-300 dark:border-slate-700 flex flex-col items-start space-y-1">
                            <div className="w-56 border-b border-slate-700 dark:border-slate-300 mb-2"></div>
                            <p className="font-bold text-slate-900 dark:text-white uppercase text-xs">
                              {formData.administrador_nombre || 'LA ADMINISTRACIÓN'}
                            </p>
                            <p className="text-[11px] text-slate-600 dark:text-slate-400">
                              Administrador y Representante Legal
                            </p>
                            <p className="text-[11px] text-slate-500">
                              {formData.copropiedad_nombre}
                            </p>
                          </div>

                        </div>
                      ) : (
                        /* Modo Edición / Código */
                        <div className="space-y-2">
                          <div className="flex items-center justify-between text-xs text-muted-foreground pb-1">
                            <span>Modo de edición manual activado. Puede personalizar el texto antes de copiarlo o imprimirlo:</span>
                          </div>
                          <Textarea
                            value={generatedResult.letter.content}
                            onChange={(e) => {
                              const val = e.target.value;
                              setGeneratedResult(prev => prev ? {
                                ...prev,
                                letter: {
                                  ...prev.letter,
                                  content: val
                                }
                              } : null);
                            }}
                            className="text-xs font-mono min-h-[360px] bg-white dark:bg-slate-900 leading-relaxed p-4 border-slate-300 focus:border-orange-500"
                          />
                        </div>
                      )}

                      {/* Compliance Legal Badges */}
                      <div className="mt-4 pt-3 border-t flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                        <span className="font-semibold text-foreground text-[11px]">Sustento Normativo Certificado:</span>
                        <Badge variant="outline" className="text-[10px] bg-white dark:bg-slate-900 border-slate-300">Art. 48 Ley 675 (Mérito Ejecutivo)</Badge>
                        <Badge variant="outline" className="text-[10px] bg-white dark:bg-slate-900 border-slate-300">Art. 30 Ley 675 (Tasa Máxima de Usura)</Badge>
                        <Badge variant="outline" className="text-[10px] bg-white dark:bg-slate-900 border-slate-300">Art. 422 Código General del Proceso</Badge>
                        <Badge variant="outline" className="text-[10px] bg-white dark:bg-slate-900 border-slate-300">Ley 1266 de 2008 (Habeas Data)</Badge>
                        <Badge variant="outline" className="text-[10px] bg-white dark:bg-slate-900 border-slate-300">Ley 2300 de 2023</Badge>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ) : (
                <Card className="h-full min-h-[420px] flex items-center justify-center border-dashed text-center p-8 text-muted-foreground">
                  <div className="space-y-3 max-w-sm">
                    <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center text-muted-foreground/60">
                      <FileText className="w-6 h-6" />
                    </div>
                    <h3 className="font-medium text-sm text-foreground">Sin requerimiento generado</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      Seleccione un deudor del desplegable o complete los datos a la izquierda para generar la carta formal, el título ejecutivo PDF y el copy de WhatsApp.
                    </p>
                  </div>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>

        {/* ── PESTAÑA 2: DIRECTORIO PH (NUEVO CRUD) ── */}
        <TabsContent value="directory" className="space-y-6">
          <Card className="shadow-sm border">
            <CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Users className="h-4 w-4 text-orange-600" />
                  Directorio de Copropietarios e Inmuebles
                </CardTitle>
                <CardDescription className="text-xs">
                  Administre el censo de copropietarios, datos de contacto y saldos para autocompletado en un clic.
                </CardDescription>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleOpenCreateDebtorModal}
                  className="bg-orange-600 hover:bg-orange-700 text-white gap-1.5 h-8 text-xs font-medium"
                >
                  <Plus className="h-3.5 w-3.5" /> Nuevo Copropietario
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Buscador */}
              <div className="flex items-center gap-2 max-w-md">
                <div className="relative flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por Torre/Apto, Propietario o Cédula..."
                    value={debtorSearchQuery}
                    onChange={e => setDebtorSearchQuery(e.target.value)}
                    className="pl-8 text-xs h-9"
                  />
                </div>
                {debtorSearchQuery && (
                  <Button variant="ghost" size="sm" onClick={() => setDebtorSearchQuery('')} className="h-9 text-xs">
                    Limpiar
                  </Button>
                )}
              </div>

              {/* Tabla Directorio */}
              {filteredDebtors.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground space-y-2">
                  <Users className="h-8 w-8 mx-auto text-muted-foreground/50" />
                  <p className="text-sm font-medium">No se encontraron copropietarios registrados.</p>
                  <p className="text-xs">Haga clic en "+ Nuevo Copropietario" o cargue una planilla en la pestaña "Carga Masiva Excel".</p>
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead>PH / Conjunto</TableHead>
                        <TableHead>Dirección</TableHead>
                        <TableHead>Unidad Privada</TableHead>
                        <TableHead>Propietario</TableHead>
                        <TableHead>Cédula / NIT</TableHead>
                        <TableHead>WhatsApp</TableHead>
                        <TableHead>Correo</TableHead>
                        <TableHead>Saldo Total</TableHead>
                        <TableHead>Días Mora</TableHead>
                        <TableHead>Etapa</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredDebtors.map(debtor => {
                        const tot = debtor.saldo_total || (debtor.saldo_capital + debtor.saldo_intereses);
                        return (
                          <TableRow key={debtor.id || debtor.unidad_privada} className="text-xs hover:bg-muted/40">
                            <TableCell className="font-medium text-foreground whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                                <span>{debtor.copropiedad_nombre || settings.nombre_copropiedad || 'Copropiedad'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                <span>{debtor.direccion || '-'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold text-foreground whitespace-nowrap">{debtor.unidad_privada}</TableCell>
                            <TableCell className="whitespace-nowrap">{debtor.propietario_nombre}</TableCell>
                            <TableCell className="font-mono text-muted-foreground whitespace-nowrap">{debtor.propietario_id || '-'}</TableCell>
                            <TableCell className="font-mono text-muted-foreground whitespace-nowrap">{debtor.telefono || '-'}</TableCell>
                            <TableCell className="text-muted-foreground whitespace-nowrap">{debtor.email || '-'}</TableCell>
                            <TableCell className="font-semibold text-foreground whitespace-nowrap">
                              ${tot?.toLocaleString('es-CO')} COP
                            </TableCell>
                            <TableCell className="font-medium whitespace-nowrap">{debtor.dias_mora} d</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  debtor.dias_mora > 60
                                    ? 'bg-rose-50 text-rose-700 border-rose-300'
                                    : debtor.dias_mora > 30
                                      ? 'bg-amber-50 text-amber-700 border-amber-300'
                                      : 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                }
                              >
                                {debtor.etapa_cobro || 'prejuridico'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setActiveTab('liquidador');
                                    toast.info(`Cargando liquidador con tasas SFC para ${debtor.unidad_privada}`);
                                  }}
                                  className="h-7 text-xs gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  title="Liquidar Intereses Superfinanciera"
                                >
                                  <Calculator className="h-3 w-3" /> Liquidar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setActiveTab('litigation');
                                    toast.info(`Cargando módulo judicial para demandar ${debtor.unidad_privada}`);
                                  }}
                                  className="h-7 text-xs gap-1 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                  title="Iniciar Demanda Ejecutiva y Embargo"
                                >
                                  <Gavel className="h-3 w-3" /> Demandar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setActiveTab('agreements');
                                    toast.info(`Abriendo simulador de acuerdos para ${debtor.unidad_privada}`);
                                  }}
                                  className="h-7 text-xs gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                                  title="Pactar Acuerdo de Pago"
                                >
                                  <Handshake className="h-3 w-3" /> Pactar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleQuickChargeDebtor(debtor)}
                                  className="h-7 text-xs gap-1 text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                                  title="Cargar a Cobro Individual"
                                >
                                  <Send className="h-3 w-3" /> Cobrar
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleOpenEditDebtorModal(debtor)}
                                  className="h-7 text-xs gap-1 text-blue-600 hover:text-blue-700"
                                  title="Editar"
                                >
                                  <Edit className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteDebtor(debtor)}
                                  className="h-7 text-xs gap-1 text-rose-600 hover:text-rose-700"
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── PESTAÑA: ACUERDOS DE PAGO & PORTAL DEL DEUDOR ── */}
        <TabsContent value="agreements" className="space-y-6">
          <PaymentAgreementsTab
            debtors={registeredDebtors}
            copropiedadNombre={settings.nombre_copropiedad || formData.copropiedad_nombre}
            copropiedadNit={settings.nit || formData.copropiedad_nit}
            administradorNombre={settings.administrador_nombre || formData.administrador_nombre}
          />
        </TabsContent>

        {/* ── PESTAÑA: COBRO JURÍDICO & DEMANDAS EJECUTIVAS ── */}
        <TabsContent value="litigation" className="space-y-6">
          <LitigationTab
            debtors={registeredDebtors}
            copropiedadNombre={settings.nombre_copropiedad || formData.copropiedad_nombre}
            copropiedadNit={settings.nit || formData.copropiedad_nit}
            administradorNombre={settings.administrador_nombre || formData.administrador_nombre}
          />
        </TabsContent>

        {/* ── PESTAÑA: LIQUIDADOR FINANCIERO SUPERFINANCIERA (NUEVO) ── */}
        <TabsContent value="liquidador" className="space-y-6">
          <FinancialLiquidadorJudicial
            debtors={registeredDebtors}
            copropiedadNombre={settings.nombre_copropiedad || formData.copropiedad_nombre}
            copropiedadNit={settings.nit || formData.copropiedad_nit}
            onApplyCalculatedSaldos={(cap, inte, unidad) => {
              setFormData(prev => ({
                ...prev,
                unidad_privada: unidad || prev.unidad_privada,
                saldo_capital: String(cap),
                saldo_intereses: String(inte)
              }));
              setActiveTab('generator');
            }}
          />
        </TabsContent>

        {/* ── PESTAÑA 3: CARGA MASIVA EXCEL CON ANALÍTICA & GRÁFICOS ── */}
        <TabsContent value="bulk" className="space-y-6">
          {/* Zona Superior: Subida de Archivo y Descarga de Plantilla */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2 shadow-sm border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-5 w-5 text-emerald-600" />
                    <span>Importar Planilla de Cartera (Excel / CSV)</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadTemplate}
                    className="gap-1.5 text-xs h-8 border-emerald-300 text-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-950"
                  >
                    <Download className="h-3.5 w-3.5" /> Descargar Plantilla Modelo .xlsx
                  </Button>
                </CardTitle>
                <CardDescription className="text-xs">
                  Cargue el estado de cartera de la copropiedad. Las unidades se sincronizan automáticamente con el directorio institucional.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-emerald-200 dark:border-emerald-900 hover:border-emerald-400 dark:hover:border-emerald-700 bg-emerald-50/30 dark:bg-emerald-950/10 rounded-xl p-8 text-center cursor-pointer transition-colors"
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".xlsx,.xls,.csv"
                    className="hidden"
                  />
                  <div className="space-y-2">
                    <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-950 text-emerald-600 flex items-center justify-center">
                      {isUploadingFile ? <RefreshCw className="h-6 w-6 animate-spin" /> : <Upload className="h-6 w-6" />}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-emerald-950 dark:text-emerald-200">
                        {isUploadingFile ? 'Analizando planilla de cartera...' : 'Haga clic para subir archivo Excel (.xlsx, .xls) o CSV'}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Detección inteligente de columnas: Torre, Apto, Propietario, Cédula, Teléfono, Correo, Saldo Capital, Intereses y Días Mora.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tarjeta Informativa de Ley 675 y Ley 2300 */}
            <Card className="shadow-sm border bg-slate-50/50 dark:bg-slate-900/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs font-semibold flex items-center gap-1.5 text-slate-800 dark:text-slate-200">
                  <ShieldAlert className="h-4 w-4 text-blue-600" />
                  Reglas de Campañas Masivas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2.5 text-xs text-muted-foreground">
                <p>
                  <strong>Sincronización Automática:</strong> Al subir la planilla, todos los deudores quedan guardados en el Directorio para autocompletar cobros individuales futuros.
                </p>
                <p>
                  <strong>Mérito Ejecutivo (Art. 48 Ley 675):</strong> Puede generar el certificado formal en PDF en 1 clic para cualquier unidad de la lista.
                </p>
                <p>
                  <strong>Pagos PSE:</strong> Cada mensaje de WhatsApp y correo incluye un link exclusivo para pago inmediato.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Estadísticas de la Planilla Importada */}
          {batchSummary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card className="p-3 shadow-sm border">
                <p className="text-[11px] text-muted-foreground font-medium">Unidades en Mora</p>
                <p className="text-xl font-bold text-foreground mt-1">{batchSummary.total_unidades}</p>
              </Card>
              <Card className="p-3 shadow-sm border">
                <p className="text-[11px] text-muted-foreground font-medium">Cartera Total</p>
                <p className="text-xl font-bold text-orange-600 mt-1">${batchSummary.total_deuda.toLocaleString('es-CO')}</p>
              </Card>
              <Card className="p-3 shadow-sm border border-emerald-200 bg-emerald-50/30">
                <p className="text-[11px] text-emerald-800 font-medium">Verde (&lt;30d)</p>
                <p className="text-xl font-bold text-emerald-600 mt-1">{batchSummary.semaforo.verde}</p>
              </Card>
              <Card className="p-3 shadow-sm border border-amber-200 bg-amber-50/30">
                <p className="text-[11px] text-amber-800 font-medium">Amarillo (31-60d)</p>
                <p className="text-xl font-bold text-amber-600 mt-1">{batchSummary.semaforo.amarillo}</p>
              </Card>
              <Card className="p-3 shadow-sm border border-rose-200 bg-rose-50/30">
                <p className="text-[11px] text-rose-800 font-medium">Rojo (&gt;60d Prejudicial)</p>
                <p className="text-xl font-bold text-rose-600 mt-1">{batchSummary.semaforo.rojo}</p>
              </Card>
            </div>
          )}

          {/* ── PANEL DE ANALÍTICA VISUAL (RECHARTS) ── */}
          {batchSummary && importedDebtors.length > 0 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Gráfico Donut */}
              <Card className="shadow-sm border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <PieIcon className="h-4 w-4 text-orange-600" />
                    Distribución por Etapa de Mora
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Composición de unidades según días de mora acumulados.
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={75}
                        paddingAngle={4}
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <RechartsTooltip />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Gráfico de Barras */}
              <Card className="shadow-sm border">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <BarChart3 className="h-4 w-4 text-blue-600" />
                    Cartera por Torre / Bloque
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Saldos totales adeudados discriminados por sector.
                  </CardDescription>
                </CardHeader>
                <CardContent className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={barChartTowerData} margin={{ top: 10, right: 10, left: 10, bottom: 20 }}>
                      <XAxis dataKey="torre" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={(val) => `$${(val / 1000000).toFixed(1)}M`} />
                      <RechartsTooltip formatter={(val: any) => [`$${val?.toLocaleString('es-CO')} COP`, 'Deuda']} />
                      <Bar dataKey="saldo" fill="#f97316" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Top 5 Deudores */}
              <Card className="shadow-sm border bg-slate-50/50 dark:bg-slate-900/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-rose-600" />
                    Top 5 Mayores Deudores
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Inmuebles que concentran el mayor porcentaje de mora.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-2.5">
                  {topDebtors.map((item, idx) => (
                    <div key={item.unidad_privada} className="flex items-center justify-between text-xs p-2 rounded bg-white dark:bg-slate-900 border">
                      <div className="truncate pr-2">
                        <span className="font-semibold text-slate-800 dark:text-slate-200">
                          {idx + 1}. {item.unidad_privada}
                        </span>
                        <p className="text-[11px] text-muted-foreground truncate">{item.propietario_nombre}</p>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <span className="font-bold text-rose-600">${item.saldo_total?.toLocaleString('es-CO')}</span>
                        <p className="text-[10px] text-muted-foreground">{item.porcentaje}% del total</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Tabla de Morosos Masivos */}
          {importedDebtors.length > 0 && (
            <Card className="shadow-sm border">
              <CardHeader className="pb-3 flex flex-col md:flex-row md:items-center justify-between gap-3">
                <div>
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Users className="h-4 w-4 text-emerald-600" />
                    Detalle de Unidades y Selección de Envío
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Seleccione los copropietarios a los que desea notificar o descargue su Título Ejecutivo formal (Art. 48).
                  </CardDescription>
                </div>

                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Canal:</Label>
                    <Select
                      value={batchChannelOption}
                      onValueChange={(val: any) => setBatchChannelOption(val)}
                    >
                      <SelectTrigger className="h-8 text-xs w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="both">WhatsApp + Correo</SelectItem>
                        <SelectItem value="whatsapp">Solo WhatsApp</SelectItem>
                        <SelectItem value="email">Solo Correo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    size="sm"
                    disabled={isBatchSending || selectedUnits.size === 0}
                    onClick={handleBatchSend}
                    className="gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-medium text-xs h-8 shadow"
                  >
                    {isBatchSending ? (
                      <>
                        <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                        Despachando Campaña...
                      </>
                    ) : (
                      <>
                        <Send className="h-3.5 w-3.5" />
                        Disparar Campaña Masiva ({selectedUnits.size} Unidades)
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>

              <CardContent>
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead className="w-10">
                          <Checkbox
                            checked={selectedUnits.size === importedDebtors.length && importedDebtors.length > 0}
                            onCheckedChange={toggleSelectAll}
                          />
                        </TableHead>
                        <TableHead>PH / Conjunto</TableHead>
                        <TableHead>Dirección</TableHead>
                        <TableHead>Unidad Privada</TableHead>
                        <TableHead>Propietario</TableHead>
                        <TableHead>Teléfono / Móvil</TableHead>
                        <TableHead>Correo Electrónico</TableHead>
                        <TableHead>Saldo Total</TableHead>
                        <TableHead>Días Mora</TableHead>
                        <TableHead>Riesgo</TableHead>
                        <TableHead>Link PSE</TableHead>
                        <TableHead className="text-right">Título Art. 48</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importedDebtors.map((debtor) => {
                        const isSelected = selectedUnits.has(debtor.unidad_privada);
                        const tot = debtor.saldo_total || (debtor.saldo_capital + debtor.saldo_intereses);
                        return (
                          <TableRow key={debtor.unidad_privada} className={`text-xs ${isSelected ? 'bg-muted/30' : ''}`}>
                            <TableCell>
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleSelectUnit(debtor.unidad_privada)}
                              />
                            </TableCell>
                            <TableCell className="font-medium text-foreground whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                                <span>{debtor.copropiedad_nombre || settings.nombre_copropiedad || 'Copropiedad'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground whitespace-nowrap">
                              <div className="flex items-center gap-1.5">
                                <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                                <span>{debtor.direccion || '-'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="font-semibold text-foreground whitespace-nowrap">{debtor.unidad_privada}</TableCell>
                            <TableCell className="whitespace-nowrap">{debtor.propietario_nombre}</TableCell>
                            <TableCell className="font-mono text-muted-foreground whitespace-nowrap">{debtor.telefono || '-'}</TableCell>
                            <TableCell className="text-muted-foreground whitespace-nowrap">{debtor.email || '-'}</TableCell>
                            <TableCell className="font-semibold text-foreground whitespace-nowrap">
                              ${tot?.toLocaleString('es-CO')} COP
                            </TableCell>
                            <TableCell className="font-medium whitespace-nowrap">{debtor.dias_mora} d</TableCell>
                            <TableCell>
                              <Badge
                                variant="outline"
                                className={
                                  debtor.dias_mora > 60
                                    ? 'bg-rose-50 text-rose-700 border-rose-300 font-semibold'
                                    : debtor.dias_mora > 30
                                      ? 'bg-amber-50 text-amber-700 border-amber-300'
                                      : 'bg-emerald-50 text-emerald-700 border-emerald-300'
                                }
                              >
                                {debtor.dias_mora > 60 ? 'Prejudicial' : debtor.dias_mora > 30 ? 'Prejurídico' : 'Preventivo'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {debtor.payment_link ? (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => window.open(ensureProductionPaymentLink(debtor.payment_link), '_blank')}
                                  className="h-7 text-xs gap-1 text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                                >
                                  <ExternalLink className="h-3 w-3" /> PSE
                                </Button>
                              ) : (
                                <span className="text-muted-foreground text-[11px]">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDownloadTituloPDF(debtor)}
                                className="h-7 text-[11px] gap-1 text-rose-700 border-rose-200 hover:bg-rose-50 dark:text-rose-300 dark:border-rose-900"
                              >
                                <FileDown className="h-3 w-3" /> PDF
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── PESTAÑA 4: CONTROL Y BITÁCORA ── */}
        <TabsContent value="logs" className="space-y-4">
          <Card className="shadow-sm border">
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Clock className="h-4 w-4 text-orange-600" />
                  Historial de Requerimientos y Notificaciones Despachadas
                </CardTitle>
                <CardDescription className="text-xs">
                  Trazabilidad inmutable de todas las comunicaciones enviadas a copropietarios morosos.
                </CardDescription>
              </div>

              <Badge variant="secondary" className="text-xs">
                {logs.length} Notificaciones Registradas
              </Badge>
            </CardHeader>

            <CardContent>
              {logs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground space-y-2">
                  <AlertCircle className="h-8 w-8 mx-auto text-muted-foreground/50" />
                  <p className="text-sm">No hay registros de notificaciones aún.</p>
                  <p className="text-xs">Los requerimientos enviados por Email o WhatsApp aparecerán aquí con su estado y fecha.</p>
                </div>
              ) : (
                <div className="rounded-md border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="text-xs">
                        <TableHead>Fecha</TableHead>
                        <TableHead>PH / Conjunto</TableHead>
                        <TableHead>Dirección</TableHead>
                        <TableHead>Unidad Privada</TableHead>
                        <TableHead>Propietario</TableHead>
                        <TableHead>Canal</TableHead>
                        <TableHead>Saldo Total</TableHead>
                        <TableHead>Etapa</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => (
                        <TableRow key={log.id} className="text-xs hover:bg-muted/40">
                          <TableCell className="font-mono whitespace-nowrap text-muted-foreground">
                            {new Date(log.created_at).toLocaleString('es-CO', {
                              dateStyle: 'short',
                              timeStyle: 'short'
                            })}
                          </TableCell>
                          <TableCell className="font-medium text-foreground whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                              <span>{log.copropiedad_nombre || settings.nombre_copropiedad || 'Copropiedad'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-muted-foreground whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <MapPin className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <span>{log.direccion || '-'}</span>
                            </div>
                          </TableCell>
                          <TableCell className="font-semibold whitespace-nowrap">{log.unidad_privada}</TableCell>
                          <TableCell className="whitespace-nowrap">{log.propietario_nombre}</TableCell>
                          <TableCell whitespace-nowrap>
                            <Badge
                              variant="outline"
                              className={log.canal === 'whatsapp' ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : 'border-blue-300 text-blue-700 bg-blue-50'}
                            >
                              {log.canal === 'whatsapp' ? 'WhatsApp' : 'Correo'}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-semibold">
                            ${log.saldo_total?.toLocaleString('es-CO')} COP
                          </TableCell>
                          <TableCell>
                            <span className="capitalize text-muted-foreground">{log.etapa_cobro.replace('_', ' ')}</span>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={log.estado === 'sent' || log.estado === 'direct_link_ready' ? 'secondary' : 'destructive'}
                              className="text-[11px]"
                            >
                              {log.estado === 'sent' ? 'Enviado' : log.estado === 'direct_link_ready' ? 'Enlace Preparado' : 'Fallido'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {log.direct_link && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => window.open(log.direct_link, '_blank')}
                                className="h-7 text-xs gap-1 text-emerald-600 hover:text-emerald-700"
                              >
                                <ExternalLink className="h-3 w-3" /> Abrir WA
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── PESTAÑA 5: ORQUESTADOR DE TAREAS PROGRAMADAS & AUTOMATIZACIÓN (INSPIRADO EN CELERY BEAT) ── */}
        <TabsContent value="settings" className="space-y-6">
          <CobranzaSchedulerHub
            settings={settings}
            onSaveSettings={handleSaveSettingsFromHub}
            registeredDebtors={registeredDebtors}
            onTriggerBatchSend={handleSchedulerTriggerBatchSend}
            onOpenWhatsAppConfig={() => setIsWhatsAppConfigOpen(true)}
          />
        </TabsContent>
      </Tabs>

      {/* ── MODAL DIALOG: CREAR / EDITAR COPROPIETARIO (CRUD) ── */}
      <Dialog open={isDebtorDialogOpen} onOpenChange={setIsDebtorDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Users className="h-5 w-5 text-orange-600" />
              <span>{editingDebtor ? 'Editar Ficha de Copropietario' : 'Registrar Nuevo Copropietario'}</span>
            </DialogTitle>
            <DialogDescription className="text-xs">
              Complete los datos del inmueble y los saldos de expensas comunes.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3.5 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium">PH o Conjunto Residencial</Label>
                <Input
                  placeholder="Ej: Torres del Parque"
                  value={debtorModalForm.copropiedad_nombre || ''}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, copropiedad_nombre: e.target.value })}
                  className="text-xs h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Dirección Inmueble</Label>
                <Input
                  placeholder="Ej: Calle 123 # 45-67, Bogotá"
                  value={debtorModalForm.direccion || ''}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, direccion: e.target.value })}
                  className="text-xs h-8"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs font-medium">Unidad Privada *</Label>
                <Input
                  placeholder="Ej: Torre 1 Apto 402"
                  value={debtorModalForm.unidad_privada}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, unidad_privada: e.target.value })}
                  className="text-xs h-8"
                  required
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Nombre Completo *</Label>
                <Input
                  placeholder="Nombre del copropietario"
                  value={debtorModalForm.propietario_nombre}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, propietario_nombre: e.target.value })}
                  className="text-xs h-8"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs font-medium">Cédula / NIT</Label>
                <Input
                  placeholder="1020304050"
                  value={debtorModalForm.propietario_id || ''}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, propietario_id: e.target.value })}
                  className="text-xs h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">WhatsApp / Móvil</Label>
                <Input
                  placeholder="3001234567"
                  value={debtorModalForm.telefono || ''}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, telefono: e.target.value })}
                  className="text-xs h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Correo</Label>
                <Input
                  type="email"
                  placeholder="propietario@email.com"
                  value={debtorModalForm.email || ''}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, email: e.target.value })}
                  className="text-xs h-8"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div className="space-y-1">
                <Label className="text-xs font-medium">Saldo Capital ($)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={debtorModalForm.saldo_capital}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, saldo_capital: parseFloat(e.target.value) || 0 })}
                  className="text-xs h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Intereses ($)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={debtorModalForm.saldo_intereses}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, saldo_intereses: parseFloat(e.target.value) || 0 })}
                  className="text-xs h-8"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-medium">Días Mora</Label>
                <Input
                  type="number"
                  placeholder="30"
                  value={debtorModalForm.dias_mora}
                  onChange={e => setDebtorModalForm({ ...debtorModalForm, dias_mora: parseInt(e.target.value, 10) || 0 })}
                  className="text-xs h-8"
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Etapa de Cobranza</Label>
              <Select
                value={debtorModalForm.etapa_cobro}
                onValueChange={val => setDebtorModalForm({ ...debtorModalForm, etapa_cobro: val })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="preventivo">Preventivo (1-30 días)</SelectItem>
                  <SelectItem value="prejuridico">Prejurídico Formal (31-60 días)</SelectItem>
                  <SelectItem value="prejudicial_aviso_demanda">Prejudicial / Aviso Demanda (&gt;60 días)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium">Notas u Observaciones</Label>
              <Textarea
                placeholder="Ej: Acuerdos previos, horario preferido de llamada, etc."
                value={debtorModalForm.notas || ''}
                onChange={e => setDebtorModalForm({ ...debtorModalForm, notas: e.target.value })}
                rows={2}
                className="text-xs resize-none"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" size="sm" onClick={() => setIsDebtorDialogOpen(false)} className="text-xs h-8">
              Cancelar
            </Button>
            <Button size="sm" onClick={handleSaveDebtorModal} className="bg-orange-600 hover:bg-orange-700 text-white text-xs h-8">
              Guardar Ficha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── MODAL DE CONFIGURACIÓN Y DIAGNÓSTICO DE WHATSAPP BUSINESS API ── */}
      <WhatsAppConfigModal
        isOpen={isWhatsAppConfigOpen}
        onClose={() => setIsWhatsAppConfigOpen(false)}
        onConfigSaved={(saved) => setWhatsAppConfig(saved)}
      />
    </div>
  );
}
